import { Geometry, Web3DViewer } from '@technology/web3d';
import { TrimbimModel } from '@technology/web3d-plugin-trimbim';
import {
  LineSelectionSink,
  LineSelectionSourceId,
} from './line-selection-sink';
import { extractStitchedLinePaths } from './trimbim-line-paths';

/** Source id used for paths published by this source. */
const TRIMBIM_SECTION_SOURCE = LineSelectionSourceId.trimbim;

/**
 * Listens to viewer selection events and, whenever the selection lands on a
 * loaded {@link TrimbimModel} entity whose geometry contains a line-like
 * chunk (positions only, no indices/normals), publishes that polyline to a
 * {@link LineSelectionSink} so it can drive section generation.
 *
 * This is a plain class, NOT a web3d `Capability`: it does not extend a
 * capability base, hold capability state, or register with the viewer's
 * `CapabilityRegistry`. It is a {@link LineSelectionSink} *source* — it reads
 * the viewer's current selection and the geometry of already-loaded models
 * and forwards line geometry to the sink. The sink's enable toggle and
 * per-source bookkeeping decide whether anything is rendered.
 *
 * Model-agnostic: it sections lines from *any* TrimBIM model (alignments,
 * models-panel imports, design packages, …).
 */
export class TrimbimLineSelectionSource {
  private readonly viewer: Web3DViewer;
  private sink: LineSelectionSink | undefined;
  private unregisterSectionRefresher: (() => void) | undefined;
  /**
   * Monotonic counter bumped by every selection event AND by detach. The
   * async geometry walk re-checks this after each `await` so a later
   * selection/detach can pre-empt an older publish without leaving the sink
   * in an inconsistent state.
   */
  private publishGeneration = 0;

  constructor(viewer: Web3DViewer) {
    this.viewer = viewer;
    this.viewer.addEventListener('selection', this.viewerEventListener);
  }

  /**
   * Wires this source to a {@link LineSelectionSink}. Calling again replaces
   * any previously-attached sink. Returns nothing — use
   * {@link detachLineSelectionSections} or {@link cleanup} to unwire.
   */
  public attachLineSelectionSections(sink: LineSelectionSink): void {
    this.unregisterSectionRefresher?.();
    this.sink = sink;
    this.unregisterSectionRefresher = sink.registerSource(
      TRIMBIM_SECTION_SOURCE,
      () => {
        void this.publishSelectedSectionPaths();
      }
    );
  }

  /**
   * Reverses {@link attachLineSelectionSections}. Safe to call when no sink
   * is attached.
   */
  public detachLineSelectionSections(): void {
    this.unregisterSectionRefresher?.();
    this.unregisterSectionRefresher = undefined;
    this.sink = undefined;
    // Invalidate any in-flight publish so its post-await commit aborts.
    this.publishGeneration++;
  }

  /** Removes the selection listener and detaches from any sink. */
  public async cleanup(): Promise<void> {
    this.detachLineSelectionSections();
    this.viewer.removeEventListener('selection', this.viewerEventListener);
    await Promise.resolve();
  }

  private readonly viewerEventListener = (): void => {
    void this.publishSelectedSectionPaths();
  };

  /**
   * Re-publishes section paths for the currently-selected TrimBIM entities.
   * Used both as the selection-change handler and as the sink's refresh hook
   * (for "toggle section generation on with something already selected").
   */
  private async publishSelectedSectionPaths(): Promise<void> {
    const sink = this.sink;
    if (!sink) {
      return;
    }
    const myGen = ++this.publishGeneration;
    sink.clearSource(TRIMBIM_SECTION_SOURCE);

    // Pass the constructor so the viewer filters to actual TrimBIM models
    // (the bare generic overload would just cast every loaded model).
    const trbModels = this.viewer.getModels(TrimbimModel);
    for (const trbModel of trbModels) {
      const selectedIds = this.viewer.selection.get(trbModel.modelId);
      if (!selectedIds || selectedIds.length === 0) {
        continue;
      }
      // Fetch every selected entity's geometry in one call and stitch across
      // all of them: an alignment is frequently selected as MANY entity ids,
      // and joining per-entity would leave it broken into a path per entity.
      let geometry: Geometry[][];
      try {
        geometry = await trbModel.getGeometry(selectedIds);
      } catch {
        continue;
      }
      if (this.publishGeneration !== myGen) {
        return;
      }
      const linePaths = extractStitchedLinePaths(trbModel.modelId, geometry);
      for (const { pathId, path } of linePaths) {
        sink.setPath(TRIMBIM_SECTION_SOURCE, pathId, path);
      }
    }
  }
}
